import Hls, { type HlsConfig } from 'hls.js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getSessionToken, imageUrl, storage } from '../api'
import { useFeatures } from '../features'
import type { AudioTrack, MediaInfo, SubtitleTrack } from '../protocol'
import { REACTIONS } from '../protocol'
import { t, useI18n } from '../i18n'
import { formatTime, type RoomClient, type RoomState } from '../room/client'
import { Icon } from '../ui/Icon'
import { Popover } from '../ui/Popover'
import { fallibleCodec, undecodable } from './codecs'
import { HdrBadge, HdrHint, hdrState, useHdrScreen } from './hdr'
import { HotkeysHelp, Osd, type OsdMessage } from './Hotkeys'
import { EmptyScreen, Reactions, WaitingCard } from './overlays'
import { SeekBar } from './SeekBar'
import { AutoQuality, isCompressedLevel, loadQuality, type Quality, saveQuality } from './quality'
import { SubtitleSearchDialog } from './SubtitleSearch'
import { SubtitleRenderer } from './subtitles'
import { formatDelay, SubtitleSettings } from './SubtitleSettings'
import { clamp, DELAY_RANGE, loadStyle, POSITION_RANGE, saveStyle, SIZE_RANGE, styleVars, type SubtitleStyle } from './subStyle'
import { SyncEngine } from './sync'

const AUDIO_PREF = 'chillio.audio'
const SUBS_PREF = 'chillio.subs'
const REMAINING_KEY = 'chillio.remainingTime'
const FILL_KEY = 'chillio.fill'
// Arrow presses within this time add up to one seek.
const NUDGE_MS = 450
const OSD_MS = 900
// How long the seek bar holds a committed position until the room confirms it.
const PENDING_SEEK_MS = 5000

// Torrent-backed segments can take a while to arrive; be patient.
const loadPolicy = {
  default: {
    maxTimeToFirstByteMs: 90_000,
    maxLoadTimeMs: 180_000,
    timeoutRetry: { maxNumRetry: 8, retryDelayMs: 1000, maxRetryDelayMs: 5000 },
    errorRetry: { maxNumRetry: 8, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
  },
}

const hlsConfig: Partial<HlsConfig> = {
  maxBufferLength: 40,
  maxMaxBufferLength: 90,
  backBufferLength: 60,
  fragLoadPolicy: loadPolicy,
  playlistLoadPolicy: loadPolicy,
  manifestLoadPolicy: loadPolicy,
  steeringManifestLoadPolicy: loadPolicy,
  enableWorker: true,
  // The session header for the Discord Activity, where the cookie may be refused.
  xhrSetup: (xhr) => {
    const session = getSessionToken()
    if (session) xhr.setRequestHeader('Authorization', `Session ${session}`)
  },
}

export function detectCaps() {
  const ms = window.MediaSource ?? (window as unknown as { ManagedMediaSource?: typeof MediaSource }).ManagedMediaSource
  const ok = (codec: string) => !!ms?.isTypeSupported?.(`video/mp4; codecs="${codec}"`)
  return { hevc: ok('hvc1.2.4.L120.90') && !undecodable.has('hevc'), av1: ok('av01.0.08M.10') && !undecodable.has('av1') }
}

// Fatal media errors within this window mean the codec does not decode.
const DECODE_FAILURE_WINDOW = 30_000

function capsQuery() {
  const c = detectCaps()
  const vp9 = !!window.MediaSource?.isTypeSupported?.('video/mp4; codecs="vp09.00.40.08"')
  return `hevc=${c.hevc ? 1 : 0}&av1=${c.av1 ? 1 : 0}&vp9=${vp9 ? 1 : 0}`
}

function pickAudio(tracks: AudioTrack[]): AudioTrack | undefined {
  const pref = storage.get(AUDIO_PREF)
  if (pref) {
    const [name, lang, nth] = pref.split('\n')
    // Untitled tracks share names ("RUS"); the ordinal tells them apart.
    const named = tracks.filter((t) => t.name === name)
    const match = named[Number(nth) || 0] ?? named[0] ?? tracks.find((t) => !!lang && t.language === lang)
    if (match) return match
  }
  return tracks.find((t) => t.default) ?? tracks[0]
}

function pickSubs(tracks: SubtitleTrack[]): SubtitleTrack | null {
  const usable = tracks.filter((t) => t.format === 'ass' || t.format === 'vtt')
  const pref = storage.get(SUBS_PREF)
  if (pref === 'off') return null
  if (pref) {
    const [name, lang] = pref.split('\n')
    return usable.find((t) => t.name === name) ?? usable.find((t) => !!lang && t.language === lang) ?? null
  }
  return usable.find((t) => t.forced) ?? null
}

/** Downloaded from OpenSubtitles (ids "os<file>"), not part of the video. */
function isDownloaded(t: SubtitleTrack) {
  return t.id.startsWith('os')
}

/** A downloaded track in the language followed from the previous episode. */
function followedSubs(tracks: SubtitleTrack[], lang: string | null): SubtitleTrack | null {
  if (lang === null) return null
  return tracks.find((t) => isDownloaded(t) && (t.language ?? '') === lang) ?? null
}

function bufferedRanges(v: HTMLVideoElement): [number, number][] {
  const out: [number, number][] = []
  for (let i = 0; i < v.buffered.length; i++) out.push([v.buffered.start(i), v.buffered.end(i)])
  return out
}

interface Props {
  client: RoomClient
  state: RoomState
  /**
   * Theater mode: the picture fills the whole page. It stands in for full
   * screen where the page may not take it (the Discord Activity).
   */
  theater?: boolean
  onTheater?: (on: boolean) => void
}

type Menu = 'audio' | 'subs' | 'subsStyle' | 'react' | 'quality'

export function Player({ client, state, theater = false, onTheater }: Props) {
  // Re-render on language changes.
  useI18n()
  const wrapRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const subsRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<SyncEngine | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const subsRendererRef = useRef<SubtitleRenderer | null>(null)
  const reactBtn = useRef<HTMLButtonElement>(null)
  const audioBtn = useRef<HTMLButtonElement>(null)
  const subsBtn = useRef<HTMLButtonElement>(null)
  const qualityBtn = useRef<HTMLButtonElement>(null)
  const fullscreenBtn = useRef<HTMLButtonElement>(null)

  const media: MediaInfo | undefined = state.media?.status === 'ready' ? state.media.info : undefined
  const role = state.you?.role
  const canControl = role === 'owner' || role === 'controller'
  const features = useFeatures()
  // OpenSubtitles search, for those who control playback.
  const [subSearch, setSubSearch] = useState(false)
  const pb = state.playback

  const [audioId, setAudioId] = useState<string | null>(null)
  const [subsId, setSubsId] = useState<string | null>(null)
  const audioIdRef = useRef(audioId)
  audioIdRef.current = audioId
  const subsIdRef = useRef(subsId)
  subsIdRef.current = subsId
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(() => Number(storage.get('chillio.volume') ?? '1'))
  // Key repeat outpaces renders; hotkeys read and write these directly.
  const volumeRef = useRef(volume)
  volumeRef.current = volume
  const [autoplayBlocked, setAutoplayBlocked] = useState(false)
  // Ticks re-render the time display; the value is read from the engine.
  const [, setTick] = useState(0)
  const [buffered, setBuffered] = useState<[number, number][]>([])
  const [scrub, setScrub] = useState<number | null>(null)
  const [pendingSeek, setPendingSeek] = useState<{ pos: number; seq: number } | null>(null)
  const [menu, setMenu] = useState<Menu | null>(null)
  const [idle, setIdle] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  // The video codec the server delivers; H.264 for an HEVC file means a transcode.
  const [deliveredCodec, setDeliveredCodec] = useState('')
  const screenHdr = useHdrScreen()
  const hdr = media ? hdrState(media, deliveredCodec, screenHdr) : null
  const [subStyle, setSubStyle] = useState<SubtitleStyle>(loadStyle)
  const subStyleRef = useRef(subStyle)
  subStyleRef.current = subStyle
  const [osd, setOsd] = useState<OsdMessage | null>(null)
  const [help, setHelp] = useState(false)
  const [remaining, setRemaining] = useState(() => storage.get(REMAINING_KEY) === '1')
  const [pip, setPip] = useState(false)
  // The subtitle track C turns back on.
  const lastSubs = useRef<SubtitleTrack | null>(null)
  // The language of OpenSubtitles subtitles on for the previous episode:
  // the server finds them for this one, and they are turned on on arrival.
  const followSubs = useRef<string | null>(null)

  // Sync engine lives as long as the room connection.
  useEffect(() => {
    const video = videoRef.current!
    const engine = new SyncEngine(video, client.clock, (s) => client.send({ t: 'status', ...s }))
    engine.setAutoplayBlockedHandler(() => setAutoplayBlocked(true))
    engine.setPlayback(client.getState().playback)
    engineRef.current = engine
    const off = client.onPlayback((p) => engine.setPlayback(p))
    const timer = window.setInterval(() => {
      setTick((n) => n + 1)
      setBuffered(bufferedRanges(video))
    }, 250)
    return () => {
      off()
      window.clearInterval(timer)
      engine.destroy()
      engineRef.current = null
    }
  }, [client])

  // Counts streams reopened without a codec that would not decode.
  const [codecFallbacks, setCodecFallbacks] = useState(0)

  // Quality: the viewer's choice; "auto" drops to the transcode for the
  // media in autoDownFor once the connection shows it cannot keep up.
  const [quality, setQuality] = useState<Quality>(loadQuality)
  const [autoDownFor, setAutoDownFor] = useState('')
  const compressed = quality === 'compressed' || (quality === 'auto' && !!media && autoDownFor === media.id)
  // Whether the stream delivered is the transcode (also when the original
  // cannot be decoded here).
  const [deliveredCompressed, setDeliveredCompressed] = useState(false)
  const stateRef = useRef(state)
  stateRef.current = state
  const autoRef = useRef<AutoQuality | null>(null)

  // One HLS session per media (reopened on a codec fallback or a quality change).
  useEffect(() => {
    const video = videoRef.current!
    const engine = engineRef.current!
    if (!media) {
      engine.setEnabled(false, 0)
      return
    }
    const src = `/api/media/${media.id}/master.m3u8?${capsQuery()}${compressed ? '&quality=compressed' : ''}`
    const preferred = pickAudio(media.audio)
    setAudioId(preferred?.id ?? null)
    if (!Hls.isSupported()) {
      // Native HLS (older iOS).
      video.src = src
      const onMeta = () => engine.setEnabled(true, media.duration)
      video.addEventListener('loadedmetadata', onMeta, { once: true })
      return () => {
        video.removeEventListener('loadedmetadata', onMeta)
        video.removeAttribute('src')
        video.load()
      }
    }
    setDeliveredCodec('')
    const hls = new Hls({ ...hlsConfig, startPosition: engine.target() })
    hlsRef.current = hls
    // hls.js may pick another audio track by itself after load errors and
    // retries; the viewer's choice is put back. A track that keeps failing
    // is left to hls.js after a few attempts.
    let corrections: number[] = []
    const keepAudio = () => {
      const want = hlsAudioIndex(hls, audioIdRef.current)
      if (want < 0 || hls.audioTrack === want) return
      const now = Date.now()
      corrections = corrections.filter((at) => now - at < 10_000)
      if (corrections.length >= 5) return
      corrections.push(now)
      if (hls.audioTrack >= 0) console.warn(`audio track ${hls.audioTrack} instead of ${want}, switching back`)
      hls.audioTrack = want
    }
    hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, keepAudio)
    hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, keepAudio)
    hls.on(Hls.Events.FRAG_LOADED, (_, data) => {
      const auto = autoRef.current
      const level = hls.levels[0]
      if (!auto || data.frag.type !== 'main' || !level || isCompressedLevel(level.url[0])) return
      const l = data.frag.stats.loading
      auto.segment(data.frag.stats.loaded, l.end - l.first, level.bitrate)
    })
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setDeliveredCodec(hls.levels[0]?.videoCodec ?? '')
      setDeliveredCompressed(isCompressedLevel(hls.levels[0]?.url[0]))
      audioIdRef.current = preferred?.id ?? null
      keepAudio()
      engine.setEnabled(true, media.duration)
    })
    let mediaErrors: number[] = []
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!data.fatal) return
      console.warn(`playback error: ${data.type} ${data.details}, recovering`)
      if (data.type !== Hls.ErrorTypes.MEDIA_ERROR) {
        hls.startLoad(engine.target())
        return
      }
      // A codec that fails again right after recovery cannot be decoded
      // here: the stream is reopened without it, as the transcode.
      const codec = fallibleCodec(hls.levels[0]?.videoCodec)
      const now = Date.now()
      mediaErrors = [...mediaErrors.filter((at) => now - at < DECODE_FAILURE_WINDOW), now]
      if (codec && mediaErrors.length >= 2) {
        console.warn(`${codec} does not decode here, switching to the transcode`)
        undecodable.add(codec)
        client.notify('player.codecFallback')
        setCodecFallbacks((n) => n + 1)
        return
      }
      hls.recoverMediaError()
    })
    // Temporary: the first minutes of loading go to the server log, for
    // playback that stalls where no developer tools are at hand (Discord).
    const opened = performance.now()
    const diag = (text: string) => {
      const at = performance.now() - opened
      if (at < 180_000) client.send({ t: 'diag', text: `${(at / 1000).toFixed(1)}s ${text}` })
    }
    const ranges = () => {
      const b = video.buffered
      return Array.from({ length: b.length }, (_, i) => `${b.start(i).toFixed(1)}-${b.end(i).toFixed(1)}`).join(',')
    }
    diag(`open ${src} ua=${navigator.userAgent.slice(-60)}`)
    hls.on(Hls.Events.MANIFEST_PARSED, () => diag(`manifest ${hls.levels[0]?.videoCodec} ${hls.levels[0]?.url[0]?.split('/api/media/')[1]}`))
    hls.on(Hls.Events.FRAG_LOADING, (_, d) => diag(`loading ${d.frag.type} ${d.frag.sn}`))
    hls.on(Hls.Events.FRAG_LOADED, (_, d) => {
      const l = d.frag.stats.loading
      diag(`loaded ${d.frag.type} ${d.frag.sn} ${d.frag.stats.loaded}B ttfb=${Math.round(l.first - l.start)} total=${Math.round(l.end - l.start)}ms`)
    })
    hls.on(Hls.Events.ERROR, (_, d) => diag(`error ${d.type} ${d.details} fatal=${d.fatal} ${d.response?.code ?? ''} ${d.error?.message ?? ''}`))
    const onVideo = (e: Event) =>
      diag(`video ${e.type} t=${video.currentTime.toFixed(1)} ready=${video.readyState} buf=${ranges()} ${video.error ? `err=${video.error.code} ${video.error.message}` : ''}`)
    const videoEvents = ['waiting', 'playing', 'stalled', 'error', 'seeking', 'canplay', 'pause', 'play']
    videoEvents.forEach((ev) => video.addEventListener(ev, onVideo))
    hls.loadSource(src)
    hls.attachMedia(video)
    return () => {
      videoEvents.forEach((ev) => video.removeEventListener(ev, onVideo))
      engine.setEnabled(false, 0)
      hls.destroy()
      hlsRef.current = null
    }
  }, [media?.id, codecFallbacks, compressed]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto watches the original only (the transcode is as low as it goes):
  // its segments, and how long and how often the room waits for this
  // viewer's own connection (not for the torrent).
  useEffect(() => {
    // A device that cannot decode the original already gets the transcode.
    if (quality !== 'auto' || compressed || deliveredCompressed || !media) return
    const id = media.id
    autoRef.current = new AutoQuality(() => {
      client.notify('player.autoCompressed')
      setAutoDownFor(id)
    })
    const timer = window.setInterval(() => {
      const s = stateRef.current
      const me = s.you?.userId
      const pb = s.playback
      autoRef.current?.blamed(pb.status === 'waiting' && !!me && !!pb.waitingFor?.includes(me), Date.now())
    }, 1000)
    return () => {
      window.clearInterval(timer)
      autoRef.current = null
    }
  }, [quality, compressed, deliveredCompressed, media?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const chooseQuality = (q: Quality) => {
    setQuality(q)
    saveQuality(q)
    // Auto starts from the original again.
    setAutoDownFor('')
    setMenu(null)
  }

  // Subtitles follow the media.
  useEffect(() => {
    if (!media) return
    const renderer = new SubtitleRenderer(videoRef.current!, subsRef.current!, media.id, media.fonts?.length ?? 0)
    subsRendererRef.current = renderer
    renderer.setOptions(subStyleRef.current.delay, subStyleRef.current.ass === 'plain')
    const prev = lastSubs.current
    // Only if they were on when the episode changed.
    followSubs.current = prev && isDownloaded(prev) && subsIdRef.current === prev.id ? (prev.language ?? '') : null
    const preferred = followedSubs(media.subtitles, followSubs.current) ?? pickSubs(media.subtitles)
    if (preferred && isDownloaded(preferred)) followSubs.current = null
    setSubsId(preferred?.id ?? null)
    subsIdRef.current = preferred?.id ?? null
    lastSubs.current = preferred
    renderer.select(preferred)
    return () => {
      renderer.destroy()
      subsRendererRef.current = null
    }
  }, [media?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // The previous episode's subtitles, found for this one after it opened.
  useEffect(() => {
    const track = media && followedSubs(media.subtitles, followSubs.current)
    if (!track) return
    followSubs.current = null
    subsIdRef.current = track.id
    setSubsId(track.id)
    lastSubs.current = track
    subsRendererRef.current?.select(track)
  }, [media?.subtitles]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    videoRef.current!.volume = volume
    storage.set('chillio.volume', String(volume))
  }, [volume])

  useEffect(() => {
    videoRef.current!.muted = muted
  }, [muted])

  useEffect(() => {
    saveStyle(subStyle)
    subsRendererRef.current?.setOptions(subStyle.delay, subStyle.ass === 'plain')
  }, [subStyle])

  useEffect(() => {
    const video = videoRef.current!
    const on = () => setPip(document.pictureInPictureElement === video)
    video.addEventListener('enterpictureinpicture', on)
    video.addEventListener('leavepictureinpicture', on)
    return () => {
      video.removeEventListener('enterpictureinpicture', on)
      video.removeEventListener('leavepictureinpicture', on)
    }
  }, [])

  const osdTimer = useRef(0)
  const showOsd = useCallback((text: string, icon?: string) => {
    window.clearTimeout(osdTimer.current)
    setOsd((cur) => ({ id: (cur?.id ?? 0) + 1, icon, text }))
    osdTimer.current = window.setTimeout(() => setOsd(null), OSD_MS)
  }, [])

  // Fill: the picture covers the whole player with its edges cut, instead
  // of bars where the window and the film differ in shape.
  const [fill, setFill] = useState(() => storage.get(FILL_KEY) === '1')
  const fillRef = useRef(fill)
  fillRef.current = fill
  const toggleFill = useCallback(() => {
    const next = !fillRef.current
    fillRef.current = next
    storage.set(FILL_KEY, next ? '1' : '0')
    setFill(next)
    showOsd(t(next ? 'osd.fill' : 'osd.fit'), next ? 'expand' : 'collapse')
  }, [showOsd])

  useEffect(() => {
    const onFs = () => {
      setFullscreen(document.fullscreenElement === wrapRef.current)
      setMenu(null)
    }
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  // A committed seek shows its position until the room state moves on.
  useEffect(() => {
    if (!pendingSeek) return
    if (pb.seq !== pendingSeek.seq) {
      setPendingSeek(null)
      return
    }
    const t = window.setTimeout(() => setPendingSeek(null), PENDING_SEEK_MS)
    return () => window.clearTimeout(t)
  }, [pb.seq, pendingSeek])

  // Hide controls while watching.
  const idleTimer = useRef(0)
  const wake = useCallback(() => {
    setIdle(false)
    window.clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => setIdle(true), 3000)
  }, [])

  const closeMenu = useCallback(() => setMenu(null), [])
  const toggleMenu = (m: Menu) => setMenu((cur) => (cur === m ? null : m))

  const chooseAudio = (track: AudioTrack) => {
    audioIdRef.current = track.id
    setAudioId(track.id)
    const nth = media?.audio.filter((a) => a.name === track.name).indexOf(track) ?? 0
    storage.set(AUDIO_PREF, `${track.name}\n${track.language ?? ''}\n${Math.max(0, nth)}`)
    const hls = hlsRef.current
    const idx = hls ? hlsAudioIndex(hls, track.id) : -1
    if (hls && idx >= 0) hls.audioTrack = idx
    setMenu(null)
  }

  const chooseSubs = (track: SubtitleTrack | null) => {
    followSubs.current = null
    subsIdRef.current = track?.id ?? null
    setSubsId(track?.id ?? null)
    if (track) lastSubs.current = track
    storage.set(SUBS_PREF, track ? `${track.name}\n${track.language ?? ''}` : 'off')
    subsRendererRef.current?.select(track)
    setMenu(null)
  }

  const togglePlay = useCallback(() => {
    if (!media) return
    const playing = pb.status !== 'paused'
    if (canControl) client.send(playing ? { t: 'pause' } : { t: 'play' })
    else {
      client.send({ t: 'request', kind: playing ? 'pause' : 'play' })
      client.notify(playing ? 'notice.askedPause' : 'notice.askedPlay')
    }
  }, [media, pb.status, canControl, client])

  const commitSeek = useCallback(
    (pos: number) => {
      if (!media) return
      pos = Math.min(media.duration, Math.max(0, pos))
      if (canControl) {
        setPendingSeek({ pos, seq: client.getState().playback.seq })
        client.send({ t: 'seek', pos })
      } else {
        client.send({ t: 'request', kind: 'seek', pos })
        client.notify('notice.askedSeek', { time: formatTime(pos) })
      }
    },
    [media, canControl, client],
  )

  // Full screen, or theater mode where full screen is not allowed.
  const canFullscreen = document.fullscreenEnabled
  // Inside Discord the frame's permissions policy forbids full screen
  // (fullscreenEnabled is false, requestFullscreen rejects), so the button
  // switches theater mode there instead.
  const toggleFullscreen = useCallback(() => {
    if (!canFullscreen) onTheater?.(!theater)
    else if (document.fullscreenElement) void document.exitFullscreen()
    else void wrapRef.current?.requestFullscreen().catch(() => {})
  }, [canFullscreen, onTheater, theater])
  const expanded = canFullscreen ? fullscreen : theater

  const togglePip = useCallback(() => {
    const video = videoRef.current!
    if (document.pictureInPictureElement) void document.exitPictureInPicture()
    else if (document.pictureInPictureEnabled) void video.requestPictureInPicture().catch(() => {})
  }, [])

  // Arrow presses add up: one seek (or one request to the owner) per burst.
  const nudgeRef = useRef<{ from: number; to: number; timer: number } | null>(null)
  const nudge = useCallback(
    (delta: number) => {
      if (!media) return
      const n = nudgeRef.current
      const from = n?.from ?? engineRef.current!.target()
      const to = Math.min(media.duration, Math.max(0, (n?.to ?? from) + delta))
      window.clearTimeout(n?.timer)
      setScrub(to)
      const timer = window.setTimeout(() => {
        nudgeRef.current = null
        setScrub(null)
        commitSeek(to)
      }, NUDGE_MS)
      nudgeRef.current = { from, to, timer }
      const total = Math.round(to - from)
      showOsd(t('osd.seek', { delta: `${total > 0 ? '+' : total < 0 ? '−' : ''}${Math.abs(total)}`, time: formatTime(to) }), total < 0 ? 'rewind' : 'forward')
    },
    [media, commitSeek, showOsd],
  )

  const usableSubs = media?.subtitles.filter((s) => s.format === 'ass' || s.format === 'vtt') ?? []

  // Keys of the physical keyboard (e.code), so they work in any layout.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || help) return
      const el = e.target instanceof Element ? e.target : document.body
      if (el.closest('input, textarea, select, [contenteditable]')) return
      // Focused buttons and menus handle Space and Enter themselves.
      if ((e.code === 'Space' || e.key === 'Enter') && el.closest('button, [role="menu"], [role="radio"]')) return
      const handled = () => {
        e.preventDefault()
        wake()
      }
      const code = e.code
      if (code === 'Escape' && theater && menu === null) {
        handled()
        onTheater?.(false)
        return
      }
      if (code === 'Slash' && e.shiftKey) {
        handled()
        setHelp(true)
        return
      }
      if (!media) return
      const digit = /^(?:Digit|Numpad)(\d)$/.exec(code)
      if (digit) {
        handled()
        const pos = (media.duration * Number(digit[1])) / 10
        commitSeek(pos)
        showOsd(formatTime(pos))
        return
      }
      switch (code) {
        case 'Space':
        case 'KeyK':
          handled()
          togglePlay()
          if (canControl) showOsd('', pb.status === 'paused' ? 'play' : 'pause')
          return
        case 'ArrowLeft':
        case 'ArrowRight':
          handled()
          nudge(code === 'ArrowLeft' ? -5 : 5)
          return
        case 'KeyJ':
        case 'KeyL':
          handled()
          nudge(code === 'KeyJ' ? -10 : 10)
          return
        case 'Home':
        case 'End':
          handled()
          commitSeek(code === 'Home' ? 0 : media.duration - 3)
          return
        case 'ArrowUp':
        case 'ArrowDown': {
          handled()
          const v = Math.min(1, Math.max(0, Math.round((volumeRef.current + (code === 'ArrowUp' ? 0.05 : -0.05)) * 100) / 100))
          volumeRef.current = v
          setVolume(v)
          setMuted(false)
          showOsd(t('osd.volume', { n: Math.round(v * 100) }), v === 0 ? 'volume-off' : 'volume')
          return
        }
        case 'KeyM':
          handled()
          setMuted(!muted)
          showOsd(muted ? t('osd.volume', { n: Math.round(volume * 100) }) : t('osd.muted'), muted ? 'volume' : 'volume-off')
          return
        case 'KeyA': {
          handled()
          if (media.audio.length < 2) return
          const next = media.audio[(media.audio.findIndex((a) => a.id === audioIdRef.current) + 1) % media.audio.length]
          chooseAudio(next)
          showOsd(t('osd.audio', { name: trackName(next, media.audio.indexOf(next)) }), 'voice')
          return
        }
        case 'KeyC': {
          handled()
          if (!usableSubs.length) return showOsd(t('osd.noSubs'), 'subtitles')
          const track = subsIdRef.current ? null : lastSubs.current && usableSubs.includes(lastSubs.current) ? lastSubs.current : usableSubs[0]
          chooseSubs(track)
          showOsd(track ? t('osd.subs', { name: trackName(track, media.subtitles.indexOf(track)) }) : t('osd.subsOff'), 'subtitles')
          return
        }
        case 'KeyS': {
          handled()
          if (!usableSubs.length) return showOsd(t('osd.noSubs'), 'subtitles')
          // Cycles through the tracks and "off".
          const i = usableSubs.findIndex((s) => s.id === subsIdRef.current)
          const track = i + 1 < usableSubs.length ? usableSubs[i + 1] : null
          chooseSubs(track)
          showOsd(track ? t('osd.subs', { name: trackName(track, media.subtitles.indexOf(track)) }) : t('osd.subsOff'), 'subtitles')
          return
        }
        case 'Equal':
        case 'NumpadAdd':
        case 'Minus':
        case 'NumpadSubtract': {
          handled()
          const size = clamp(subStyleRef.current.size + (code === 'Equal' || code === 'NumpadAdd' ? 0.1 : -0.1), SIZE_RANGE)
          subStyleRef.current = { ...subStyleRef.current, size }
          setSubStyle(subStyleRef.current)
          showOsd(t('osd.subsSize', { n: Math.round(size * 100) }), 'subtitles')
          return
        }
        case 'KeyG':
        case 'KeyH': {
          handled()
          const delay = clamp(subStyleRef.current.delay + (code === 'KeyH' ? 0.1 : -0.1), DELAY_RANGE)
          subStyleRef.current = { ...subStyleRef.current, delay }
          setSubStyle(subStyleRef.current)
          showOsd(t('osd.subsDelay', { n: formatDelay(delay) }), 'subtitles')
          return
        }
        case 'KeyF':
          handled()
          toggleFullscreen()
          return
        case 'KeyZ':
          handled()
          toggleFill()
          return
        case 'KeyI':
          handled()
          togglePip()
          return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Media Session: the title in the browser and system media controls,
  // media keys, and the browser's own picture-in-picture (Chrome offers it
  // in its media controls and when switching tabs during playback).
  const { lang } = useI18n()
  const catalog = state.title
  const sessionTitle = (catalog && (catalog.titles[lang] || catalog.titles.original)) || media?.name || ''
  useEffect(() => {
    const ms = navigator.mediaSession
    if (!ms || !media) return
    ms.metadata = new MediaMetadata({
      title: sessionTitle,
      artist: catalog?.season ? t('catalog.season', { n: catalog.season }) : media.name,
      artwork: catalog?.poster ? [{ src: imageUrl('w342', catalog.poster)!, sizes: '342x513', type: 'image/jpeg' }] : [],
    })
  }, [media, sessionTitle, catalog?.season, catalog?.poster])

  const actions = useRef({ togglePlay, nudge, togglePip })
  actions.current = { togglePlay, nudge, togglePip }
  useEffect(() => {
    const ms = navigator.mediaSession
    if (!ms) return
    const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        ms.setActionHandler(action, handler)
      } catch {
        // the browser does not know this action
      }
    }
    set('play', () => actions.current.togglePlay())
    set('pause', () => actions.current.togglePlay())
    set('seekbackward', (d) => actions.current.nudge(-(d.seekOffset ?? 10)))
    set('seekforward', (d) => actions.current.nudge(d.seekOffset ?? 10))
    set('enterpictureinpicture' as MediaSessionAction, () => actions.current.togglePip())
    return () => {
      for (const a of ['play', 'pause', 'seekbackward', 'seekforward', 'enterpictureinpicture'] as MediaSessionAction[]) set(a, null)
    }
  }, [])

  useEffect(() => {
    if (navigator.mediaSession) navigator.mediaSession.playbackState = pb.status === 'playing' ? 'playing' : 'paused'
  }, [pb.status])

  // Text subtitles are dragged to where the viewer wants them; the anchor is
  // the bottom center of the block, kept in percent of the picture.
  const [dragging, setDragging] = useState(false)
  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    wake()
    const el = e.currentTarget
    const box = wrapRef.current!.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const dx = e.clientX - (r.left + r.width / 2)
    const dy = r.bottom - e.clientY
    el.setPointerCapture(e.pointerId)
    const move = (ev: PointerEvent) => {
      setDragging(true)
      let x = ((ev.clientX - dx - box.left) / box.width) * 100
      const y = ((box.bottom - ev.clientY - dy) / box.height) * 100
      if (Math.abs(x - 50) < 2) x = 50 // snap to the center
      setSubStyle((s) => ({
        ...s,
        x: Math.round(Math.min(POSITION_RANGE.x.max, Math.max(POSITION_RANGE.x.min, x)) * 10) / 10,
        y: Math.round(Math.min(POSITION_RANGE.y.max, Math.max(POSITION_RANGE.y.min, y)) * 10) / 10,
      }))
    }
    const end = () => {
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', end)
      el.removeEventListener('pointercancel', end)
      setDragging(false)
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', end)
    el.addEventListener('pointercancel', end)
  }

  // A click on the picture pauses, a double click goes full screen. Touch
  // taps only show the controls, and viewers do not send requests by
  // clicking around.
  const clickTimer = useRef(0)
  const pointerType = useRef('mouse')
  const onVideoClick = () => {
    window.clearTimeout(clickTimer.current)
    if (pointerType.current !== 'mouse' || !canControl || !media) return
    clickTimer.current = window.setTimeout(() => {
      togglePlay()
      showOsd('', pb.status === 'paused' ? 'play' : 'pause')
    }, 220)
  }
  const onVideoDoubleClick = () => {
    window.clearTimeout(clickTimer.current)
    if (pointerType.current === 'mouse') toggleFullscreen()
  }

  const unmute = () => {
    setMuted(false)
    setAutoplayBlocked(false)
    videoRef.current!.muted = false
    void videoRef.current!.play().catch(() => {})
  }

  const duration = media?.duration ?? 0
  const shown = scrub ?? pendingSeek?.pos ?? engineRef.current?.target() ?? 0
  const waiting = pb.status === 'waiting'
  const paused = pb.status === 'paused'
  const controlsVisible = !idle || paused || waiting || menu !== null || scrub !== null
  const audioIndex = media?.audio.findIndex((a) => a.id === audioId) ?? -1
  const audioTrack = media?.audio[audioIndex]
  const subsIndex = media?.subtitles.findIndex((s) => s.id === subsId) ?? -1
  const subsTrack = media?.subtitles[subsIndex]
  const audioLabel = audioTrack ? trackName(audioTrack, audioIndex) : t('player.noAudio')
  const qualityLabel =
    quality === 'compressed'
      ? '720p'
      : quality === 'original'
        ? t('player.qualityOriginal')
        : deliveredCompressed
          ? `${t('player.qualityAuto')} · 720p`
          : t('player.qualityAuto')
  // The original asked for, the transcode delivered: this device cannot
  // decode it (codec, or Discord without hardware acceleration).
  const originalUnavailable = deliveredCompressed && !compressed
  const subsLabel = subsTrack ? trackName(subsTrack, subsIndex) : t('player.subsOff')

  return (
    <div
      ref={wrapRef}
      className={`player${controlsVisible ? '' : ' player--idle'}${menu === 'subsStyle' ? ' player--subs-preview' : ''}${fill ? ' player--fill' : ''}`}
      style={styleVars(subStyle) as React.CSSProperties}
      onPointerMove={wake}
      onPointerDown={(e) => {
        pointerType.current = e.pointerType
        wake()
      }}
    >
      <video ref={videoRef} className="player__video" playsInline preload="auto" onClick={onVideoClick} onDoubleClick={onVideoDoubleClick} />
      <div
        className={`player__cues${dragging ? ' is-dragging' : ''}${menu === 'subsStyle' ? ' is-previewing' : ''}`}
        data-keeps-popover
        onPointerDown={startDrag}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        title={t('subs.positionHint')}
      >
        <div ref={subsRef} className="player__cues-text" aria-live="off" />
        {menu === 'subsStyle' && <span className="cue cue--sample">{t('subs.drag')}</span>}
      </div>

      {!media && <EmptyScreen state={state} canControl={canControl} />}

      {state.connection === 'reconnecting' ? (
        <div className="waiting" role="status">
          <p className="waiting__title waiting__title--source">{t('wait.offline')}</p>
          <p className="waiting__stats">{t('wait.offlineHint')}</p>
          <span className="waiting__indeterminate" aria-hidden="true" />
        </div>
      ) : (
        media && waiting && <WaitingCard client={client} state={state} />
      )}

      {media && paused && (
        <button type="button" className="player__center" onClick={togglePlay}>
          <Icon name="play" />
          <span>{canControl ? t('player.play') : t('player.askPlay')}</span>
        </button>
      )}

      {autoplayBlocked && (
        <button type="button" className="player__unmute" onClick={unmute}>
          <Icon name="volume-off" /> {t('player.unmute')}
        </button>
      )}

      <Reactions reactions={state.reactions} />
      <HdrHint state={hdr} />
      <Osd msg={osd} />
      {help && <HotkeysHelp onClose={() => setHelp(false)} />}

      {media && (
        <div className="controls">
          <SeekBar
            duration={duration}
            value={shown}
            buffered={buffered}
            onScrub={setScrub}
            onCommit={commitSeek}
            label={canControl ? t('player.seek') : t('player.askSeek')}
          />
          <div className="controls__row">
            <button
              type="button"
              className="icon-btn"
              onClick={togglePlay}
              aria-label={paused ? t('player.play') : t('player.pause')}
              title={canControl ? t('player.withKey', { label: paused ? t('player.play') : t('player.pause'), key: 'K' }) : t('player.askTitle')}
            >
              <Icon name={paused ? 'play' : 'pause'} />
            </button>
            <div className="volume">
              <button
                type="button"
                className="icon-btn"
                onClick={() => setMuted(!muted)}
                aria-label={muted ? t('player.unmute') : t('player.mute')}
                title={t('player.withKey', { label: muted ? t('player.unmute') : t('player.mute'), key: 'M' })}
              >
                <Icon name={muted || volume === 0 ? 'volume-off' : 'volume'} />
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                style={{ '--vol': `${(muted ? 0 : volume) * 100}%` } as React.CSSProperties}
                onChange={(e) => {
                  setVolume(Number(e.target.value))
                  setMuted(false)
                }}
                aria-label={t('player.volume')}
              />
            </div>
            <button
              type="button"
              className="controls__time"
              onClick={() => {
                storage.set(REMAINING_KEY, remaining ? '0' : '1')
                setRemaining(!remaining)
              }}
              title={remaining ? t('player.elapsed') : t('player.remaining')}
            >
              {remaining ? `−${formatTime(Math.max(0, duration - shown))}` : formatTime(shown)} <span className="muted">/ {formatTime(duration)}</span>
            </button>
            <span className="controls__spacer" />
            <button
              ref={reactBtn}
              type="button"
              className="icon-btn"
              onClick={() => toggleMenu('react')}
              aria-label={t('player.reaction')}
              aria-expanded={menu === 'react'}
            >
              <Icon name="smile" />
            </button>
            {hdr && <HdrBadge media={media} state={hdr} />}
            <button
              ref={audioBtn}
              type="button"
              className="text-btn"
              onClick={() => toggleMenu('audio')}
              disabled={media.audio.length === 0}
              aria-expanded={menu === 'audio'}
              aria-label={t('player.audioAria', { name: audioLabel })}
            >
              <Icon name="voice" /> <span className="text-btn__label">{audioLabel}</span>
            </button>
            <button
              ref={qualityBtn}
              type="button"
              className="text-btn"
              onClick={() => toggleMenu('quality')}
              aria-expanded={menu === 'quality'}
              aria-label={t('player.qualityAria', { name: qualityLabel })}
            >
              <Icon name="settings" /> <span className="text-btn__label">{qualityLabel}</span>
            </button>
            <button
              ref={subsBtn}
              type="button"
              className="text-btn"
              onClick={() => toggleMenu('subs')}
              aria-expanded={menu === 'subs'}
              aria-label={t('player.subsAria', { name: subsLabel })}
            >
              <Icon name="subtitles" /> <span className="text-btn__label">{subsLabel}</span>
            </button>
            {document.pictureInPictureEnabled && (
              <button
                type="button"
                className={`icon-btn controls__pip${pip ? ' is-on' : ''}`}
                onClick={togglePip}
                aria-pressed={pip}
                aria-label={t('player.pip')}
                title={t('player.withKey', { label: t('player.pip'), key: 'I' })}
              >
                <Icon name="pip" />
              </button>
            )}
            {expanded && (
              <button
                type="button"
                className={`icon-btn${fill ? ' is-on' : ''}`}
                onClick={toggleFill}
                aria-pressed={fill}
                aria-label={t('player.fill')}
                title={t('player.withKey', { label: t('player.fill'), key: 'Z' })}
              >
                <Icon name="fill" />
              </button>
            )}
            {(canFullscreen || onTheater) && (
              <button
                ref={fullscreenBtn}
                type="button"
                className="icon-btn"
                onClick={toggleFullscreen}
                aria-label={fullscreenLabel(canFullscreen, expanded)}
                title={t('player.withKey', { label: fullscreenLabel(canFullscreen, expanded), key: 'F' })}
              >
                <Icon name={expanded ? 'collapse' : 'expand'} />
              </button>
            )}
          </div>

          {/* At the right edge, so that the panel covers little of the sample. */}
          <Popover anchor={canFullscreen || onTheater ? fullscreenBtn : subsBtn} open={menu === 'subsStyle'} onClose={closeMenu} className="popover--settings" label={t('player.subsSettings')}>
            <button type="button" className="popover__back" onClick={() => setMenu('subs')}>
              <Icon name="back" /> <span>{t('subs.back')}</span>
            </button>
            <SubtitleSettings style={subStyle} onChange={setSubStyle} ass={subsTrack?.format === 'ass'} />
          </Popover>

          <Popover anchor={reactBtn} open={menu === 'react'} onClose={closeMenu} className="popover--reactions" label={t('player.reactions')}>
            {REACTIONS.map((r) => (
              <button key={r} type="button" onClick={() => client.send({ t: 'react', emoji: r })} aria-label={t('player.reactionOne', { emoji: r })}>
                {r}
              </button>
            ))}
          </Popover>

          <Popover anchor={qualityBtn} open={menu === 'quality'} onClose={closeMenu} label={t('player.quality')}>
            <p className="popover__title">{t('player.quality')}</p>
            <button type="button" className={quality === 'auto' ? 'is-active' : ''} onClick={() => chooseQuality('auto')}>
              <span>{t('player.qualityAuto')}</span>
              <span className="muted">
                {quality === 'auto' && deliveredCompressed ? t('player.qualityAutoNow') : t('player.qualityAutoHint')}
              </span>
            </button>
            <button type="button" className={quality === 'original' ? 'is-active' : ''} onClick={() => chooseQuality('original')}>
              <span>{t('player.qualityOriginal')}</span>
              <span className="muted">
                {originalUnavailable
                  ? t('player.qualityUnavailable')
                  : t('player.qualityOriginalHint', { res: originalLabel(media) })}
              </span>
            </button>
            <button type="button" className={quality === 'compressed' ? 'is-active' : ''} onClick={() => chooseQuality('compressed')}>
              <span>{t('player.qualityCompressed')}</span>
              <span className="muted">{t('player.qualityCompressedHint')}</span>
            </button>
          </Popover>

          <Popover anchor={audioBtn} open={menu === 'audio'} onClose={closeMenu} label={t('player.audio')}>
            <p className="popover__title">{t('player.audio')}</p>
            {media.audio.map((a, i) => (
              <button key={a.id} type="button" className={a.id === audioId ? 'is-active' : ''} onClick={() => chooseAudio(a)}>
                <span>{trackName(a, i)}</span>
                <span className="muted">{audioDetails(a)}</span>
              </button>
            ))}
          </Popover>

          <Popover anchor={subsBtn} open={menu === 'subs'} onClose={closeMenu} label={t('player.subs')}>
            <p className="popover__title">{t('player.subs')}</p>
            <button type="button" className={subsId === null ? 'is-active' : ''} onClick={() => chooseSubs(null)}>
              <span>{t('player.subsOff')}</span>
            </button>
            {media.subtitles.map((s, i) => {
              const supported = s.format === 'ass' || s.format === 'vtt'
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={!supported}
                  className={s.id === subsId ? 'is-active' : ''}
                  onClick={() => chooseSubs(s)}
                >
                  <span>{trackName(s, i)}</span>
                  <span className="muted">{subsDetails(s, supported)}</span>
                </button>
              )
            })}
            {canControl && features?.subtitles && (
              <button
                type="button"
                className="popover__link"
                onClick={() => {
                  setMenu(null)
                  setSubSearch(true)
                }}
              >
                <Icon name="search" /> <span>{t('subsearch.open')}</span>
              </button>
            )}
            <button type="button" className="popover__link" onClick={() => setMenu('subsStyle')}>
              <Icon name="settings" /> <span>{t('player.subsSettings')}</span>
            </button>
          </Popover>
          {subSearch && (
            <SubtitleSearchDialog
              roomId={state.room!.id}
              title={state.title}
              media={media}
              onAdded={(track) => chooseSubs(track)}
              onClose={() => setSubSearch(false)}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * "2160p HEVC": what the original is. Wide films (1920×800) are 1080p to
 * everyone, so the class goes by the width.
 */
function originalLabel(media: MediaInfo) {
  const v = media.video
  const w = v?.width ?? 0
  const res = w >= 3800 ? 2160 : w >= 2500 ? 1440 : w >= 1900 ? 1080 : w >= 1260 ? 720 : v?.height
  return [res ? `${res}p` : '', v?.codec ? v.codec.toUpperCase() : ''].filter(Boolean).join(' ')
}

/** Tracks without a title or language get a numbered name. */
function trackName(track: { name: string }, index: number) {
  return track.name || t('player.track', { n: index + 1 })
}

function audioDetails(a: AudioTrack) {
  const parts = [a.codec.toUpperCase()]
  if (a.channels > 2) parts.push(a.channels === 6 ? '5.1' : a.channels === 8 ? '7.1' : t('player.channels', { n: a.channels }))
  if (a.external) parts.push(t('player.externalFile'))
  return parts.join(', ')
}

function subsDetails(s: SubtitleTrack, supported: boolean) {
  if (!supported) return t('player.subsImage')
  const parts = [s.format === 'ass' ? 'ASS' : 'SRT']
  if (s.forced) parts.push(t('player.forced'))
  if (s.external) parts.push(t('player.externalFile'))
  return parts.join(', ')
}

/**
 * The hls.js index of an audio rendition, found by its playlist URL: names
 * and languages can repeat, URLs cannot.
 */
function hlsAudioIndex(hls: Hls, id: string | null): number {
  if (!id) return -1
  return hls.audioTracks.findIndex((t) => t.url.includes(`/a/${id}/`))
}

function fullscreenLabel(real: boolean, expanded: boolean) {
  if (real) return expanded ? t('player.exitFullscreen') : t('player.fullscreen')
  return expanded ? t('player.exitTheater') : t('player.theater')
}
